import { useCallback, useEffect, useRef, useState } from 'react';
import type { PositionAnalysis } from '@steward/shared';
import { aiApi, type AiChatMessage } from '@/lib/api/client';
import { AnalysisCards } from './AnalysisCards';

type AnalysisStatus = 'loading' | 'error' | 'ready';

/** Serialize the structured analysis into a synthetic assistant turn so chat follow-ups can reference it. */
function analysisToText(a: PositionAnalysis): string {
  return [
    `Risk: ${a.riskLevel} — ${a.headline}`,
    a.assessment,
    `Key factors: ${a.keyFactors.join('; ')}`,
    ...a.options.map((o) => `Option (${o.action}): ${o.summary}. Rationale: ${o.rationale}. Trade-offs: ${o.tradeoffs}`),
    `Suggested: ${a.suggestedAction}`,
    `Watch: ${a.watchTriggers.join('; ')}`,
  ].join('\n');
}

const analysisCache = new Map<string, PositionAnalysis>();

export function PositionAiPanel({ positionId }: { positionId: string }) {
  const [analysis, setAnalysis] = useState<PositionAnalysis | null>(null);
  const [status, setStatus] = useState<AnalysisStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [chat, setChat] = useState<AiChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streamingReply, setStreamingReply] = useState<string | null>(null);
  const [chatError, setChatError] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const generationRef = useRef(0);

  const runAnalysis = useCallback(async (force = false) => {
    if (!force && analysisCache.has(positionId)) {
      setAnalysis(analysisCache.get(positionId)!);
      setStatus('ready');
      return;
    }
    setStatus('loading');
    setErrorMessage('');
    try {
      const result = await aiApi.analyzePosition(positionId);
      analysisCache.set(positionId, result);
      setAnalysis(result);
      setStatus('ready');
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Analysis failed');
      setStatus('error');
    }
  }, [positionId]);

  useEffect(() => {
    generationRef.current += 1;
    setAnalysis(null);
    setChat([]);
    setStreamingReply(null);
    setChatError('');
    setInput('');
    void runAnalysis();
  }, [runAnalysis]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat, streamingReply]);

  const sendMessage = async () => {
    const question = input.trim();
    if (!question || streamingReply !== null || !analysis) return;
    const generation = generationRef.current;
    const isStale = () => generationRef.current !== generation;
    setChatError('');
    setInput('');
    const nextChat: AiChatMessage[] = [...chat, { role: 'user', content: question }];
    setChat(nextChat);
    setStreamingReply('');

    // Seed the model with the analysis as a prior assistant turn.
    // Clamp to the server's per-message cap, and trim nextChat's oldest turns so the
    // assembled array never exceeds the server's 40-message cap (2 seed turns + up to 38 recent).
    const messages: AiChatMessage[] = [
      { role: 'user', content: 'Analyze this position.' },
      { role: 'assistant', content: analysisToText(analysis).slice(0, 8000) },
      ...nextChat.slice(-38),
    ];

    let reply = '';
    try {
      await aiApi.chatPosition(positionId, messages, (delta) => {
        reply += delta;
        if (!isStale()) setStreamingReply(reply);
      });
      if (isStale()) return;
      setChat([...nextChat, { role: 'assistant', content: reply }]);
    } catch (e) {
      if (isStale()) return;
      setChatError(e instanceof Error ? e.message : 'Chat failed');
      if (reply) setChat([...nextChat, { role: 'assistant', content: reply }]);
    } finally {
      if (!isStale()) setStreamingReply(null);
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex items-center gap-3 py-6 text-sm text-gray-500">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        Analyzing with live quotes… (this can take up to ~30s)
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="space-y-3 py-4">
        <p className="text-sm text-red-600">{errorMessage}</p>
        <button
          type="button"
          onClick={() => void runAnalysis()}
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {analysis && <AnalysisCards analysis={analysis} />}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void runAnalysis(true)}
          className="text-xs text-gray-500 underline hover:text-gray-700"
        >
          Refresh analysis
        </button>
      </div>

      {(chat.length > 0 || streamingReply !== null) && (
        <div className="space-y-3 border-t border-gray-100 pt-3">
          {chat.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'text-right' : ''}>
              <div className={m.role === 'user'
                ? 'inline-block max-w-[85%] rounded-lg bg-blue-600 px-3 py-2 text-left text-sm text-white'
                : 'inline-block max-w-[85%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap'}>
                {m.content}
              </div>
            </div>
          ))}
          {streamingReply !== null && (
            <div>
              <div className="inline-block max-w-[85%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap">
                {streamingReply || '…'}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {chatError && <p className="text-sm text-red-600">{chatError}</p>}

      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void sendMessage(); }}
          placeholder="Ask a follow-up… e.g. what if it drops 5%?"
          className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          disabled={streamingReply !== null}
        />
        <button
          type="button"
          onClick={() => void sendMessage()}
          disabled={streamingReply !== null || !input.trim()}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </div>
  );
}
